import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaCheckCircle, 
  FaRegTimesCircle, 
  FaFolder, 
  FaUserFriends, 
  FaChartLine, 
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaFilter,
  FaSync,
  FaShieldAlt,
  FaUserCog,
  FaCog
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import FileExplorerModal from './fileexplorer';
import TeamInfoModal from './teaminfo';
import { getCookie } from './auth/cookies';
import TrainerAccess from './admin/traineraccess';

function AdminPage() {
    const [leaderboard, setLeaderboard] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [showExplorer, setShowExplorer] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [modalMode, setModalMode] = useState('team');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'asc' });
    const [activeTab, setActiveTab] = useState('leaderboard');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [adminSettings, setAdminSettings] = useState({
        show_final_score: true,
        ranking_method: 'score'
    });

    // Fetch admin settings
    const fetchAdminSettings = async () => {
        try {
            const response = await axios.get('/api/get_admin_settings/', {
                headers: { 'X-CSRFToken': getCookie('csrftoken') }
            });
            setAdminSettings(response.data);
        } catch (err) {
            console.error('Error fetching admin settings:', err);
            toast.error('Failed to load admin settings');
        }
    };

    // Update admin settings
    const updateAdminSettings = async (newSettings) => {
        try {
            await axios.post('/api/update_admin_settings/', newSettings, {
                headers: { 'X-CSRFToken': getCookie('csrftoken') }
            });
            setAdminSettings(prev => ({ ...prev, ...newSettings }));
            toast.success('Admin settings updated successfully');
            // Refresh leaderboard to show changes
            await fetchData();
        } catch (err) {
            console.error('Error updating admin settings:', err);
            toast.error('Failed to update admin settings');
        }
    };

    // Fetch data from API
    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Using Promise.all to fetch data in parallel
            const [leaderboardResponse, submissionsResponse] = await Promise.all([
                axios.get('/api/get_leaderboard/', { 
                    headers: { 'X-CSRFToken': getCookie('csrftoken') } 
                }),
                axios.get('/api/get_submissions/', { 
                    headers: { 'X-CSRFToken': getCookie('csrftoken') } 
                })
            ]);

            setLeaderboard(leaderboardResponse.data.leaderboard);
            setSubmissions(submissionsResponse.data.submissions);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load data');
            toast.error(`Error loading dashboard data: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
        fetchAdminSettings();
    }, []);

    // Handle refreshing data
    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchData();
        setIsRefreshing(false);
        toast.success('Dashboard data refreshed');
    };

    // Sort handlers
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted and filtered data
    const getSortedData = () => {
        const filteredData = leaderboard.filter(entry => 
            entry.team.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Get sort icon
    const getSortIcon = (name) => {
        if (sortConfig.key !== name) return <FaSort className="ml-1 text-gray-400" />;
        if (sortConfig.direction === 'asc') return <FaSortUp className="ml-1 text-blue-500" />;
        return <FaSortDown className="ml-1 text-blue-500" />;
    };

    // Handle opening the file explorer modal
    const openFileExplorer = (files) => {
        setSelectedFiles(files);
        setShowExplorer(true);
    };

    // Handle opening the team info modal
    const openTeamModal = (id, mode = 'team') => {
        setSelectedId(id);
        setModalMode(mode);
        setShowTeamModal(true);
    };

    // Handle verification of a submission
    const verifySubmission = async (submission_id, verify) => {
        try {
            await axios.post('/api/verify_submission/', 
                { submission_id, verify }, 
                { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
            );
            
            setLeaderboard((prevLeaderboard) =>
                prevLeaderboard.map((entry) =>
                    entry.id === submission_id ? { ...entry, verify } : entry
                )
            );
            
            toast.success(`Submission ${verify ? 'verified' : 'unverified'} successfully!`);
        } catch (err) {
            console.error('Error verifying submission:', err);
            toast.error(`Error verifying submission: ${err.message}`);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-200 pt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Header Section */}
                <header className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-secondary-dark dark:text-white flex items-center">
                                <FaShieldAlt className="mr-3" />
                                Admin Dashboard
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                                Manage submissions, verify results, and control access
                            </p>
                        </div>
                        
                        <div className="mt-4 md:mt-0 flex items-center space-x-3">
                            <button 
                                onClick={handleRefresh}
                                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center"
                                disabled={isRefreshing || isLoading}
                            >
                                <FaSync className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                        </div>
                    </div>
                </header>

                {/* Tabs Navigation */}
                <nav className="mb-6">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <ul className="flex flex-wrap -mb-px">
                            <li className="mr-2">
                                <button 
                                    className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                        activeTab === 'leaderboard' 
                                        ? 'border-secondary text-secondary dark:text-secondary-light' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                                    onClick={() => setActiveTab('leaderboard')}
                                >
                                    <FaChartLine className="mr-2" />
                                    Leaderboard
                                </button>
                            </li>
                            <li className="mr-2">
                                <button 
                                    className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                        activeTab === 'access' 
                                        ? 'border-secondary text-secondary dark:text-secondary-light' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                                    onClick={() => setActiveTab('access')}
                                >
                                    <FaUserCog className="mr-2" />
                                    Trainer Access
                                </button>
                            </li>
                            <li className="mr-2">
                                <button 
                                    className={`inline-flex items-center py-4 px-4 text-sm font-medium border-b-2 ${
                                        activeTab === 'settings' 
                                        ? 'border-secondary text-secondary dark:text-secondary-light' 
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                                    onClick={() => setActiveTab('settings')}
                                >
                                    <FaCog className="mr-2" />
                                    Admin Settings
                                </button>
                            </li>
                        </ul>
                    </div>
                </nav>

                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
                        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading data...</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 rounded-md mb-8">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <FaRegTimesCircle className="h-5 w-5 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                                    Error Loading Data
                                </h3>
                                <p className="mt-2 text-red-700 dark:text-red-400">{error}</p>
                                <button 
                                    onClick={fetchData} 
                                    className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Leaderboard Tab Content */}
                        {activeTab === 'leaderboard' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4 flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaChartLine className="mr-2" />
                                        Submission Leaderboard
                                    </h2>
                                    <span className="text-white bg-secondary px-2 py-1 rounded-full text-sm">
                                        {leaderboard.length} Entries
                                    </span>
                                </div>
                                
                                <div className="p-4">
                                    {/* Search and Filter Bar */}
                                    <div className="mb-4 flex flex-col sm:flex-row gap-3">
                                        <div className="relative flex-grow">
                                            <input
                                                type="text"
                                                placeholder="Search by team name..."
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <span className="mr-2 text-gray-600 dark:text-gray-400 hidden sm:inline">Filter:</span>
                                            <select 
                                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                onChange={(e) => setSearchTerm(e.target.value ? `verified:${e.target.value}` : '')}
                                            >
                                                <option value="">All Entries</option>
                                                <option value="true">Verified Only</option>
                                                <option value="false">Unverified Only</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    {/* Leaderboard Table */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-100 dark:bg-gray-700">
                                                <tr>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                                                        onClick={() => requestSort('rank')}
                                                    >
                                                        <div className="flex items-center">
                                                            Rank
                                                            {getSortIcon('rank')}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                                                        onClick={() => requestSort('team')}
                                                    >
                                                        <div className="flex items-center">
                                                            Team
                                                            {getSortIcon('team')}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                                                        onClick={() => requestSort('score')}
                                                    >
                                                        <div className="flex items-center">
                                                            Score
                                                            {getSortIcon('score')}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                                                        onClick={() => requestSort('verify')}
                                                    >
                                                        <div className="flex items-center">
                                                            Verified
                                                            {getSortIcon('verify')}
                                                        </div>
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Files
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                {getSortedData().length > 0 ? (
                                                    getSortedData().map((entry, index) => (
                                                        <tr 
                                                            key={index} 
                                                            className={`
                                                                hover:bg-gray-50 dark:hover:bg-gray-750
                                                                ${entry.rank <= 3 ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                                                            `}
                                                        >
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`
                                                                    inline-flex items-center justify-center w-8 h-8 rounded-full
                                                                    ${entry.rank === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : ''}
                                                                    ${entry.rank === 2 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' : ''}
                                                                    ${entry.rank === 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : ''}
                                                                    ${entry.rank > 3 ? 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400' : ''}
                                                                `}>
                                                                    {entry.rank}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <button
                                                                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center"
                                                                    onClick={() => openTeamModal(entry.team_id, 'team')}
                                                                >
                                                                    <FaUserFriends className="mr-2" />
                                                                    {entry.team}
                                                                </button>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                                                                {entry.score}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {entry.verify ? (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                        <FaCheckCircle className="mr-1" /> Verified
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                                        <FaRegTimesCircle className="mr-1" /> Unverified
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <button
                                                                    onClick={() => openFileExplorer(entry.files)}
                                                                    className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                                                                >
                                                                    <FaFolder className="mr-2" />
                                                                    View Files
                                                                </button>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <button
                                                                    onClick={() => verifySubmission(entry.id, entry.verify ? 0 : 1)}
                                                                    className={`px-4 py-2 rounded-md text-white transition-colors ${
                                                                        entry.verify 
                                                                            ? 'bg-red-500 hover:bg-red-600' 
                                                                            : 'bg-green-500 hover:bg-green-600'
                                                                    }`}
                                                                >
                                                                    {entry.verify ? 'Unverify' : 'Verify'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                                            {searchTerm 
                                                                ? 'No entries match your search criteria' 
                                                                : 'No submissions available'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Trainer Access Tab Content */}
                        {activeTab === 'access' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaUserCog className="mr-2" />
                                        Trainer Access Management
                                    </h2>
                                </div>
                                
                                <div className="p-6">
                                    <TrainerAccess onOpenTeamModal={(userId) => openTeamModal(userId, 'user')} />
                                </div>
                            </div>
                        )}

                        {/* Admin Settings Tab Content */}
                        {activeTab === 'settings' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-secondary-dark p-4">
                                    <h2 className="text-xl font-semibold text-white flex items-center">
                                        <FaCog className="mr-2" />
                                        Admin Settings
                                    </h2>
                                </div>
                                
                                <div className="p-6">
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                                Leaderboard Display Options
                                            </h3>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Show Final Score Toggle */}
                                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Show Final Score
                                                        </label>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            Display final_score column in leaderboard
                                                        </p>
                                                    </div>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={adminSettings.show_final_score}
                                                            onChange={(e) => updateAdminSettings({ show_final_score: e.target.checked })}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                                    </label>
                                                </div>

                                                {/* Ranking Method Selection */}
                                                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Ranking Method
                                                    </label>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                                        Choose which field to use for ranking teams
                                                    </p>
                                                    <select 
                                                        value={adminSettings.ranking_method}
                                                        onChange={(e) => updateAdminSettings({ ranking_method: e.target.value })}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <option value="score">Score (Original)</option>
                                                        <option value="final_score">Final Score</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Current Settings Display */}
                                        <div className="border-t pt-4">
                                            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                                Current Settings
                                            </h4>
                                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                                <ul className="space-y-2 text-sm">
                                                    <li className="flex items-center">
                                                        <span className="text-gray-600 dark:text-gray-400 mr-2">Final Score Display:</span>
                                                        <span className={`font-medium ${adminSettings.show_final_score ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                            {adminSettings.show_final_score ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                    </li>
                                                    <li className="flex items-center">
                                                        <span className="text-gray-600 dark:text-gray-400 mr-2">Ranking Method:</span>
                                                        <span className="font-medium text-blue-600 dark:text-blue-400">
                                                            {adminSettings.ranking_method === 'score' ? 'Score (Original)' : 'Final Score'}
                                                        </span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* File Explorer Modal */}
                {showExplorer && (
                    <FileExplorerModal files={selectedFiles} onClose={() => setShowExplorer(false)} />
                )}

                {/* Team Info Modal */}
                {showTeamModal && (
                    <TeamInfoModal
                        team_id={modalMode === 'team' ? selectedId : null}
                        user_id={modalMode === 'user' ? selectedId : null}
                        onClose={() => setShowTeamModal(false)}
                    />
                )}
            </div>
        </div>
    );
}

export default AdminPage;